import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { GET_CLIP } from "MVVM/GraphQL/clip";
import { RESTORE_PRUNED_CLIP } from "MVVM/GraphQL/restorePrunedClip";
import { ViewableClip, PinnedClipsAtom } from "MVVM/State/clip";
import { CLIPS_RECOMMENDED, CLIPS_PROFILE } from "MVVM/GraphQL/feed";
import { GET_USER_PINNED_CLIPS } from "MVVM/GraphQL/pinnedClips";
import { PLAYLIST } from "MVVM/GraphQL/playlist";
import { TClip } from "MVVM/@types";
import { getClipContextFromUrl } from "MVVM/Utilities/QueryParam";
import { ClipFullModalState } from "MVVM/State/modals";

interface ClipExperienceViewModelProps {
  id: string;
}

interface FeedData {
  clipsRecommended: TClip[];
}

interface UserClipsData {
  getUser: {
    clips: {
      nodes: TClip[];
    };
  };
}

interface RecommendedClipsData {
  clipsRecommended: TClip[];
}

interface PlaylistData {
  playlist: {
    id: string;
    clips: TClip[];
  };
}

const limit = 20;

const ClipExperienceViewModel = ({ id }: ClipExperienceViewModelProps) => {
  let context = getClipContextFromUrl();
  const [clipFullModalState, setClipFullModalState] =
    useRecoilState(ClipFullModalState);

  const {
    data: initialData,
    loading,
    error,
  } = useQuery(GET_CLIP, {
    variables: {
      id,
      includeMomentTimeline: true,
    },
    fetchPolicy: "network-only",
  });

  const [viewableClip, setViewableClip] = useRecoilState(ViewableClip);
  const pinnedClips = useRecoilValue(PinnedClipsAtom);
  const setPinnedClips = useSetRecoilState(PinnedClipsAtom);

  const [createPrunedClip] = useMutation(RESTORE_PRUNED_CLIP, {
    onError: () => {},
    refetchQueries: [GET_CLIP],
  });

  const [getNextClip] = useLazyQuery(GET_CLIP, { fetchPolicy: "network-only" });

  // Feed data loading logic
  const shouldFetchPinnedClips =
    context?.type === "pinned-clips" &&
    pinnedClips.length === 0 &&
    context.metadata?.userId;

  useQuery(GET_USER_PINNED_CLIPS, {
    variables: {
      userId:
        context?.type === "pinned-clips" && context.metadata?.userId
          ? context.metadata.userId
          : undefined,
    },
    skip: !shouldFetchPinnedClips,
    onCompleted: (data) => {
      if (data?.getUser?.pinnedClips) {
        setPinnedClips(data.getUser.pinnedClips);
      }
    },
  });

  const feedQueryConfig = useMemo(() => {
    switch (context?.type) {
      case "pinned-clips":
        return {
          query: CLIPS_RECOMMENDED,
          variables: { limit },
          dataTransform: (): FeedData => ({
            clipsRecommended:
              pinnedClips
                ?.slice()
                .reverse()
                .map(
                  (clip) =>
                    ({
                      ...clip,
                      _id: clip.id,
                      clipLink: `/clip/${clip.shareId}`,
                      thumbnailStandardUrl: clip.thumbnailUrl,
                    }) as unknown as TClip,
                ) || [],
          }),
          skipQuery: true,
        };
      case "profile":
        return {
          query: CLIPS_PROFILE,
          variables: {
            first: limit,
            userId: context.metadata?.userId,
            filters: {},
            after: context.metadata?.startCursor || undefined,
          },
          dataTransform: (data: UserClipsData | undefined): FeedData => ({
            clipsRecommended: data?.getUser?.clips?.nodes || [],
          }),
        };
      case "playlist":
        return {
          query: PLAYLIST,
          variables: { id: context.metadata?.playlistId },
          dataTransform: (data: PlaylistData | undefined): FeedData => {
            return {
              clipsRecommended: (data?.playlist?.clips || []).map((clip) => ({
                ...clip,
                username: clip.username || clip.user?.username,
              })),
            };
          },
        };
      case "match":
        return {
          query: CLIPS_RECOMMENDED,
          skipQuery: true,
          dataTransform: () => ({ clipsRecommended: [] }),
        };
      default:
        return {
          query: CLIPS_RECOMMENDED,
          variables: { limit },
          dataTransform: (data: RecommendedClipsData | undefined): FeedData =>
            data || { clipsRecommended: [] },
        };
    }
  }, [context, pinnedClips]);

  const shouldSkipFeedQuery = feedQueryConfig.skipQuery;

  const {
    data: feedData,
    error: feedError,
    loading: feedLoading,
  } = useQuery(feedQueryConfig.query, {
    variables: feedQueryConfig.variables,
    fetchPolicy: "cache-and-network",
    skip: shouldSkipFeedQuery,
  });

  useEffect(() => {
    if (initialData?.clip) setViewableClip(initialData.clip);
  }, [initialData, setViewableClip]);

  const data = viewableClip ? { clip: viewableClip } : initialData;

  const processedFeedData = shouldSkipFeedQuery
    ? feedQueryConfig.dataTransform?.()
    : feedQueryConfig.dataTransform?.(feedData);

  return {
    data,
    error,
    loading,
    createPrunedClip,
    feedData: processedFeedData,
    feedError,
    feedLoading,
    getNextClip,
    context,
  };
};

export { ClipExperienceViewModel };
